import React, { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Btn, Card, Field, Heading, inputCls } from './ui';
import { platformApi, errorText } from '../../services/platformApi';
import { useAuth } from '../../contexts/AuthContext';
import { LLink } from '../../lib/lang';

/** Заявка (ИС для клиники, расчёт стоимости, консультация). Сохраняется в БД, администратор видит её в панели. */
const RequestForm = ({ productSlug = null, kind = 'project', title, submitLabel, messageHint }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: user?.name || '', email: user?.email || '', phone: '', clinic: '', message: '', website: '',
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);
  const consentParts = t('pl.common.consent', { link: '\u0001' }).split('\u0001');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error(t('pl.request.consentRequired')); return; }
    setBusy(true);
    try {
      await platformApi.sendRequest({
        name: form.name, email: form.email, phone: form.phone || null, clinic: form.clinic || null,
        message: form.message, product_slug: productSlug, kind, website: form.website || null,
      });
      setDone(true);
    } catch (err) {
      toast.error(errorText(err, t('pl.request.sendError')));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Card id="request" className="text-center py-10">
        <CheckCircle2 className="mx-auto mb-3 text-green-700" size={40} />
        <Heading level={3} className="text-2xl mb-2">{t('pl.request.sentTitle')}</Heading>
        <p className="text-gray-700">{t('pl.request.sentText')}</p>
      </Card>
    );
  }

  return (
    <Card id="request">
      <Heading level={3} className="text-2xl mb-4">{title || t('pl.request.title')}</Heading>
      <form onSubmit={submit} noValidate={false}>
        <div className="grid sm:grid-cols-2 gap-x-4">
          <Field label={t('pl.request.name')}><input className={inputCls} required minLength={2} maxLength={100} value={form.name} onChange={set('name')} autoComplete="name" /></Field>
          <Field label={t('pl.request.email')}><input className={inputCls} type="email" required value={form.email} onChange={set('email')} autoComplete="email" /></Field>
          <Field label={t('pl.request.phone')}><input className={inputCls} type="tel" maxLength={25} value={form.phone} onChange={set('phone')} autoComplete="tel" /></Field>
          <Field label={t('pl.request.clinic')}><input className={inputCls} maxLength={160} value={form.clinic} onChange={set('clinic')} /></Field>
        </div>
        <Field label={t('pl.request.message')} hint={messageHint || t('pl.request.messageHint')}>
          <textarea className={inputCls} rows={5} required minLength={10} maxLength={4000} value={form.message} onChange={set('message')} />
        </Field>
        {/* Ловушка для ботов: поле скрыто от людей */}
        <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', height: 0, overflow: 'hidden' }}>
          <label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} /></label>
        </div>
        <label className="flex items-start gap-2 text-sm mb-4">
          <input type="checkbox" className="mt-1" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span>{consentParts[0]}<LLink className="underline" to="/privacy-policy" target="_blank" rel="noreferrer">{t('pl.common.privacyLink')}</LLink>{consentParts[1]}</span>
        </label>
        <Btn type="submit" loading={busy}>{submitLabel || t('pl.request.submit')}</Btn>
      </form>
    </Card>
  );
};

export default RequestForm;
